import { api } from '@store/api';
import { store } from '@store/index';
import type { PageInfo } from '#types/api';
import type { ConsultationSummary, ConsultationSort, ConsultationHistory, ConsultationHistoryQuery, ConsultationSend, ConsultationSent } from '#types/consultation';

export interface ConsultationListQuery { authEpoch: number; pageNum: number; pageSize: number; sortMode: ConsultationSort }
export const consultationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    consultationList: builder.query<PageInfo<ConsultationSummary>, ConsultationListQuery>({
      query: ({ authEpoch: _epoch, ...body }) => ({ url: '/m/consultation/list', method: 'POST', body, timeout: 15000 }),
      keepUnusedDataFor: 0,
      providesTags: ['ConsultationList'],
    }),
    consultationHistory: builder.query<ConsultationHistory, ConsultationHistoryQuery>({
      query: ({ authEpoch: _epoch, ...body }) => ({
        url: body.conversationUuid ? '/m/consultation/messages' : '/u/consultation/messages',
        // The chat hook owns the timeout so explicit cancellation also works with the installed RTK version.
        method: 'POST', body,
      }),
      keepUnusedDataFor: 0,
    }),
    consultationSend: builder.mutation<ConsultationSent, ConsultationSend>({
      query: ({ authEpoch: _epoch, ...body }) => ({
        url: body.conversationUuid ? '/m/consultation/reply' : '/u/consultation/send',
        method: 'POST', body, timeout: 15000,
      }),
      invalidatesTags: (_result, error) => error ? [] : ['ConsultationList'],
    }),
  }),
});

export const { useConsultationListQuery, useConsultationSendMutation } = consultationApi;

/** Wait out an older fetch, then issue a new one: invalidation alone may reuse an in-flight query. */
export async function refreshConsultationList(query: ConsultationListQuery): Promise<void> {
  const running = store.dispatch(consultationApi.util.getRunningQueryThunk('consultationList', query));
  if (running) await running;
  if (store.getState().user.authEpoch !== query.authEpoch) return;
  const request = store.dispatch(consultationApi.endpoints.consultationList.initiate(query, { subscribe: false, forceRefetch: true }));
  await request.unwrap();
}
